import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { fetchPulseActivity, fetchPulseInbox, fetchPulseTraffic } from "./api";
import { PulseInboxCard } from "./PulseInboxCard";
import { PulseKpiCard } from "./PulseKpiCard";
import { PulseActivityFeed } from "./PulseActivityFeed";
import { useSession } from "@/lib/auth/session";

export function PulseScreen() {
  const userName = useSession((s) => s.user?.name?.split(" ")[0] ?? "");

  const inbox = useQuery({
    queryKey: ["pulse", "inbox"],
    queryFn: fetchPulseInbox,
    refetchInterval: 60_000,
  });

  const traffic = useQuery({
    queryKey: ["pulse", "traffic"],
    queryFn: fetchPulseTraffic,
    refetchInterval: 60_000,
  });

  const activity = useQuery({
    queryKey: ["pulse", "activity"],
    queryFn: () => fetchPulseActivity(20),
    refetchInterval: 30_000,
  });

  const refreshing = inbox.isFetching || traffic.isFetching || activity.isFetching;

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Pulso</p>
          <h1 className="nx-h1">
            {greeting()}{userName ? `, ${userName}` : ""}.
          </h1>
          <p className="text-sm text-fg-muted mt-1">
            Lo que requiere tu atención y cómo se está moviendo la plataforma.
          </p>
        </div>
        <button
          onClick={() => {
            inbox.refetch();
            traffic.refetch();
            activity.refetch();
          }}
          className="nx-btn-secondary"
          disabled={refreshing}
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {/* ── Zona 1: Bandeja del día ─────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2">Bandeja del día</h2>
          {inbox.data && (
            <span className="text-2xs text-fg-subtle">
              Última actualización: {fmtTime(inbox.data.generatedAt)}
            </span>
          )}
        </div>
        {inbox.isLoading ? (
          <LoadingBlock label="Calculando pendientes…" />
        ) : inbox.isError ? (
          <ErrorBlock onRetry={() => inbox.refetch()} />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {inbox.data?.buckets.map((b) => (
              <PulseInboxCard key={b.key} bucket={b} />
            ))}
          </div>
        )}
      </section>

      {/* ── Zona 2: Pulso del portal (tráfico) ──────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2">Pulso del portal</h2>
          <span className="text-2xs text-fg-subtle">vs ayer · sparkline 7 días</span>
        </div>
        {traffic.isLoading ? (
          <LoadingBlock label="Midiendo tráfico…" />
        ) : traffic.isError ? (
          <ErrorBlock onRetry={() => traffic.refetch()} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {traffic.data?.kpis.map((kpi) => (
              <PulseKpiCard key={kpi.key} kpi={kpi} />
            ))}
          </div>
        )}
      </section>

      {/* ── Zona 3: Actividad reciente ─────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2">Actividad reciente</h2>
          <span className="text-2xs text-fg-subtle">últimos 20 eventos del sistema</span>
        </div>
        <div className="nx-card overflow-hidden">
          {activity.isLoading ? (
            <LoadingBlock label="Leyendo audit log…" />
          ) : activity.isError ? (
            <ErrorBlock onRetry={() => activity.refetch()} />
          ) : (
            <PulseActivityFeed entries={activity.data?.entries ?? []} />
          )}
        </div>
      </section>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return "Trabajando hasta tarde";
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="nx-card p-8 flex items-center justify-center gap-2 text-sm text-fg-subtle">
      <Loader2 size={14} className="animate-spin" />
      {label}
    </div>
  );
}

function ErrorBlock({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="nx-card p-6 flex flex-col items-center text-center gap-2">
      <AlertCircle size={18} className="text-danger" />
      <p className="text-sm text-fg-muted">
        No pudimos cargar este bloque. El backend puede estar desplegándose.
      </p>
      <button onClick={onRetry} className="nx-btn-secondary mt-2">
        Reintentar
      </button>
    </div>
  );
}
